import React from 'react';

export interface DateFilterOptions {
  mode: 'preset' | 'date' | 'range';
  preset?: 'hoy' | 'semana' | 'mes' | 'todos';
  date?: string;
  startDate?: string;
  endDate?: string;
}

export interface DateRangeFilterProps {
  value: DateFilterOptions;
  onChange: (value: DateFilterOptions) => void;
  showPresets?: boolean;
  className?: string;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  showPresets = true,
  className = ''
}) => {
  const today = new Date().toISOString().slice(0, 10);

  // Manejar cambio de modo
  const handleModeChange = (mode: 'preset' | 'date' | 'range') => {
    if (mode === 'preset') {
      onChange({
        mode: 'preset',
        preset: 'hoy'
      });
    } else if (mode === 'date') {
      onChange({
        mode: 'date',
        date: today
      });
    } else if (mode === 'range') {
      onChange({
        mode: 'range',
        startDate: today,
        endDate: today
      });
    }
  };

  // Manejar cambio de preset
  const handlePresetChange = (preset: 'hoy' | 'semana' | 'mes' | 'todos') => {
    onChange({
      mode: 'preset',
      preset
    });
  };

  // Manejar cambio de fecha específica
  const handleDateChange = (date: string) => {
    onChange({
      mode: 'date',
      date
    });
  };

  // Manejar cambio de rango de fechas
  const handleRangeChange = (field: 'startDate' | 'endDate', date: string) => {
    onChange({
      mode: 'range',
      startDate: field === 'startDate' ? date : value.startDate || today,
      endDate: field === 'endDate' ? date : value.endDate || today
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex flex-col space-y-4">
        {/* Selector de modo */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {showPresets && (
            <label className="flex items-center text-sm">
              <input
                type="radio"
                value="preset"
                checked={value.mode === 'preset'}
                onChange={() => handleModeChange('preset')}
                className="mr-2 text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium text-gray-700">Período predefinido</span>
            </label>
          )}
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="date"
              checked={value.mode === 'date'}
              onChange={() => handleModeChange('date')}
              className="mr-2 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">Fecha específica</span>
          </label>
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="range"
              checked={value.mode === 'range'}
              onChange={() => handleModeChange('range')}
              className="mr-2 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">Rango de fechas</span>
          </label>
        </div>

        {/* Controles según el modo */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {value.mode === 'preset' && showPresets && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <select
                value={value.preset || 'hoy'}
                onChange={(e) => handlePresetChange(e.target.value as 'hoy' | 'semana' | 'mes' | 'todos')}
                className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="hoy">Hoy</option>
                <option value="semana">Esta semana</option>
                <option value="mes">Este mes</option>
                <option value="todos">Todos</option>
              </select>
            </div>
          )}

          {value.mode === 'date' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha
              </label>
              <input
                type="date"
                value={value.date || today}
                onChange={(e) => handleDateChange(e.target.value)}
                className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          )}

          {value.mode === 'range' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha inicio
                </label>
                <input
                  type="date"
                  value={value.startDate || today}
                  onChange={(e) => handleRangeChange('startDate', e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha fin
                </label>
                <input
                  type="date"
                  value={value.endDate || today}
                  onChange={(e) => handleRangeChange('endDate', e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </>
          )}
        </div>

        {/* Indicador de selección actual */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium">Período seleccionado:</span>
            <span>
              {value.mode === 'preset' 
                ? value.preset === 'hoy' ? 'Hoy' :
                  value.preset === 'semana' ? 'Esta semana' :
                  value.preset === 'mes' ? 'Este mes' : 'Todos los registros'
                : value.mode === 'date'
                ? `${new Date(value.date + 'T12:00:00' || today).toLocaleDateString('es-ES')}`
                : `${new Date(value.startDate + 'T12:00:00' || today).toLocaleDateString('es-ES')} - ${new Date(value.endDate + 'T12:00:00' || today).toLocaleDateString('es-ES')}`
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;
